import store, { dispatch, state } from '__common/store';
import { removeKdTreePoints } from '../cursor/utils/kdTreeStore';
import { removePanelFromRtree } from '__editor/panelsEditor/components/panels/panelsCollisions';
import { drawPanel } from './utils/panelsManagment';
import { removePanelCenterPoints } from './utils/panelsCenterStore';
import { isRM10Evolution, isRM5 } from '__common/constants/products';
import { createPanelBayMapping } from './utils/createPanelBayMapping';
import { createBays } from './utils/createBays';
import { isPanelinSkewZone } from '../skewZones/drawSkewZonesOnStage';
import { canShowSkewZone } from '__common/components/exposure/exposureHelper';
import { getPanelTextureForState } from '__editor/panelsEditor/models/panel';
import { ADD_PANEL_BAY_MAPPING, ADD_VISITED_BAYS } from './panelsActions';

type PanelGraphic = PIXI.Sprite & {
  attached?: boolean;
  centerPoint?: { x: number; y: number; id: number };
  exposed?: boolean;
  exposedNeighbour?: boolean;
  groupId?: number;
  id: number;
  nearObstruction?: boolean;
  pendingRemoval?: boolean;
  roofZone?: roofZoneNumber;
  rTreeBounds?: any;
  stickPoints?: stickyPoints[];
  manualExposure?: 'E' | 'NE';
  manualExposureDisplayed?: boolean;
};

const cleanupPanelGraphic = (panelGraphic: PanelGraphic, removeFromRtree = false) => {
  if (panelGraphic.stickPoints?.length) {
    removeKdTreePoints(panelGraphic.stickPoints);
  }

  if (panelGraphic.centerPoint) {
    removePanelCenterPoints(panelGraphic.centerPoint);
  }

  if (removeFromRtree && panelGraphic.rTreeBounds) {
    removePanelFromRtree(panelGraphic.rTreeBounds);
  }

  panelGraphic.destroy({ children: true });
};

const needsPanelRedraw = (panelGraphic: PanelGraphic, panel: panelInState, displayManualExposure: boolean) =>
  panelGraphic.x !== panel.x + state().settings.canvasCenter.x - panel.width / 2
  || panelGraphic.y !== panel.y + state().settings.canvasCenter.y - panel.height / 2
  || panelGraphic.nearObstruction !== panel.nearObstruction
  || panelGraphic.exposed !== panel.exposed
  || panelGraphic.exposedNeighbour !== panel.exposedNeighbour
  || panelGraphic.attached !== panel.attached
  || panelGraphic.roofZone !== panel.roofZone
  || panelGraphic.groupId !== panel.groupId
  || panelGraphic.manualExposure !== panel.manualExposure
  || panelGraphic.manualExposureDisplayed !== displayManualExposure;

const hasSameNumberSet = (first: number[] = [], second: number[] = []) => {
  if (first.length !== second.length) {
    return false;
  }

  const firstSet = new Set(first);
  if (firstSet.size !== second.length) {
    return false;
  }

  return second.every((value) => firstSet.has(value));
};

export const drawPanelsOnStage = async(stage: PIXI.Container, panelsContainer, baysContainer, panelsDict) => {
  const { panels:{panels, panelsToBeMoved, bays: {panelBayMapping : panelBaysMapping, visitedBays}}, background: { moveArrayMode, displayManualExposure }, roofZones: { skewZone } } = store.getState();
  if (!panelsContainer) {
    return;
  }

  const movingPanelIds = moveArrayMode && panelsToBeMoved.length > 0
    ? new Set<number>(panelsToBeMoved.map(panel => panel.id))
    : new Set<number>();
  const renderedPanelIds = new Set<number>();
  let panelGraphicsChanged = false;

  if (skewZone) {
    (panelsContainer.children as PanelGraphic[]).forEach(panelGraphic => cleanupPanelGraphic(panelGraphic));
    panelsContainer.children = [];
    panelGraphicsChanged = true;
  } else {
    const nextChildren: PanelGraphic[] = [];

    (panelsContainer.children as PanelGraphic[]).forEach((panelGraphic) => {
      const panel = panelsDict[panelGraphic.id];

      if (!panel) {
        cleanupPanelGraphic(panelGraphic, true);
        panelGraphicsChanged = true;
        return;
      }

      if (needsPanelRedraw(panelGraphic, panel, displayManualExposure)) {
        cleanupPanelGraphic(panelGraphic);
        panelGraphicsChanged = true;
        return;
      }

      if (panelGraphic.pendingRemoval !== !!panel.pendingRemoval) {
        panelGraphic.texture = getPanelTextureForState(panel);
        panelGraphic.pendingRemoval = !!panel.pendingRemoval;
      }

      panelGraphic.visible = !movingPanelIds.has(panelGraphic.id);

      nextChildren.push(panelGraphic);
      renderedPanelIds.add(panelGraphic.id);
    });

    panelsContainer.children = nextChildren;
  }
  const { projectConfiguration:{ projectEnvConfig:{ allow_manual_attachments }, productId, projectVersion }, background:{ toggleAttachments, metersPerPixel }, roofsSelector: { mapType } } = state();
  
  panels.forEach((panel: panelInState) => {
    if (renderedPanelIds.has(panel.id)) {
      return;
    }

    if(canShowSkewZone(productId, mapType, projectVersion)) {
      panel.skewAffected = isPanelinSkewZone(panel, metersPerPixel);
    }
    const pixiPanel: any = drawPanel(panel);
    pixiPanel.rTreeBounds = panel.rTreeBounds;
    pixiPanel.visible = !movingPanelIds.has(panel.id);
    panelsContainer.addChild(pixiPanel);
    renderedPanelIds.add(panel.id);
    panelGraphicsChanged = true;
  });
  stage.addChild(panelsContainer);
  
  if(allow_manual_attachments && Object.keys(panelsDict).length >0 && toggleAttachments && (isRM10Evolution(productId) || isRM5(productId))){
    const panelIds = new Set(panels.map(panel => panel.id));
    const activeBayIds = new Set<number>();
    const invalidatedBayIds = new Set<number>();
    const nextPanelBaysMapping = new Map(panelBaysMapping);
    const arrayWise = panels.reduce((acc, item) => {
      const groupId = item.groupId;
      if (groupId !== undefined) {
        if (!acc[groupId]) {
          acc[groupId] = [];
        }
        acc[groupId].push(item);
      }
      return acc;
    },                              {});
    
    const rightEdgePanels: Array<number> = [];
    const leftEdgePanels: Array<number> = [];
    const northEdgePanels: number[] = [];
    const northRightEdgePanels: Array<number> = [];
    const northMostRow: Array<number>= [];
    let mappingChanged = false;

    Object.values(arrayWise).forEach((array) => {
      const {
        invalidatedBayIds: arrayInvalidatedBayIds,
        leftEdgePanels: arrayLeftEdgePanels,
        mappingChanged: arrayMappingChanged,
        northEdgePanels: arrayNorthEdgePanels,
        northMostRow: arrayNorthMostRow,
        northRightEdgePanels: arrayNorthRightEdgePanels,
        rightEdgePanels: arrayRightEdgePanels,
      } = createPanelBayMapping(array, panelsDict, nextPanelBaysMapping);

      rightEdgePanels.push(...arrayRightEdgePanels);
      leftEdgePanels.push(...arrayLeftEdgePanels);
      northEdgePanels.push(...arrayNorthEdgePanels);
      northRightEdgePanels.push(...arrayNorthRightEdgePanels);
      northMostRow.push(...arrayNorthMostRow);

      if (arrayMappingChanged) {
        mappingChanged = true;
        arrayInvalidatedBayIds.forEach((bayId) => invalidatedBayIds.add(bayId));
      }
    });

    nextPanelBaysMapping.forEach((baysAssociated, panelId) => {
      if (!panelIds.has(panelId)) {
        return;
      }

      baysAssociated.forEach((bay) => activeBayIds.add(bay.id));
    });

    const nextBayChildren = [];
    [...baysContainer.children].forEach((child: any) => {
      if (!activeBayIds.has(child.id)) {
        child.destroy({ children: true });
        return;
      }

      nextBayChildren.push(child);
    });
    baysContainer.children = nextBayChildren;

    if (panelGraphicsChanged) {
      activeBayIds.forEach((bayId) => invalidatedBayIds.add(bayId));
    }

    const nextVisitedBays = (Array.isArray(visitedBays) ? visitedBays : []).filter((bayId) => !invalidatedBayIds.has(bayId));
    const updatedVisitedBays = createBays(
      nextPanelBaysMapping,
      panelsDict,
      baysContainer,
      nextVisitedBays,
      rightEdgePanels,
      leftEdgePanels,
      northEdgePanels,
      northRightEdgePanels,
      northMostRow,
      invalidatedBayIds,
    );

    if (mappingChanged) {
      dispatch(ADD_PANEL_BAY_MAPPING(nextPanelBaysMapping));
    }

    if (!hasSameNumberSet(updatedVisitedBays, visitedBays)) {
      dispatch(ADD_VISITED_BAYS(updatedVisitedBays));
    }


    stage.addChild(baysContainer);
  } else {
    stage.removeChild(baysContainer);
  }
};


export const removeAllPanelsFromStageToRedraw = (panelsContainer: PIXI.Container, baysContainer: PIXI.Container) => {
  if (panelsContainer) {
    (panelsContainer.children as PanelGraphic[]).forEach(panel => cleanupPanelGraphic(panel));
    panelsContainer.children = [];
  }
  if (baysContainer) {
    baysContainer.children.map((panel: PIXI.Graphics) => panel.destroy({ children: true }));
    baysContainer.children = [];
  }
};
